import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp, ArrowDown, Pencil, Trash2 } from 'lucide-react';
import { WORLD_COUNTRIES, COUNTRY_ALIASES } from '../utils/worldCountries';

// (9 Oct 2026, 8.19/8.20; decisions 35, 46-53, 58, 59, 61-68) History: the calendar's
// meetings. Generalised the same day from the culinary provenance history: each
// calendar chooses (Settings > Calendar Options) a keyword (Hot Club "Culinary
// provenance:"; its value is a column), an index column (Hot Club "Country") and
// whether the menu is shown. Apéro: date and host only.
// Shown in place of the screen's own content, under its banner, so Sign Out and the
// other banner buttons stay to hand (decision 58). Opened and closed by the banner's
// "History" button on calendars that keep a history. Data: GET
// /api/calendars/:id/calendar-history (memberCalendarRoutes.js), which merges the
// recorded meetings with the calendar's own events.
//
//  - Heading "{calendar} history". Newest first.
//  - Date, the keyword's column, the index column and Host sort when their heading is
//    pressed; pressed again, the order reverses (decision 46).
//  - The next meeting stays at the top, its date in the blue pill, whatever the order.
//  - On phones: one card per meeting, and a "Sort by" drop-down instead of headings.
//  - Dishes from the app's polls show "by {name}"; recorded meetings have no names.
//  - Corrections (decision 59): the curator, the administrator and the active
//    stand-in get a pencil on past meetings. A recorded meeting can change
//    everything, its date included; a meeting from the app can change its dishes
//    (the rest belongs to the event: Edit Event). The first correction of an app
//    meeting copies it into the history record (PUT .../calendar-history/:date).
//  - Deleting (decision 61): the same people get a bin beside the pencil on recorded
//    meetings that are not also events (an event is removed with Delete Event). It
//    asks "Delete history record?" first (decision 76), like Delete Event
//    (DELETE .../calendar-history/:date).
//  - Colours, with a key under the heading (decision 63): blue = the next meeting;
//    green = a meeting that is (also) an event on the calendar; white = a meeting
//    recorded in the history only (the log, or entered by hand).
//  - Hosts (decision 68): a summary of how often each member has hosted, opened with
//    the "Hosts" button beside Back. (10 Oct 2026) It replaces the table and the key
//    while open (decision 70), and Back closes it first (decision 72). "Online" stays
//    in it (decision 69).
//  - On larger screens the column headings stay in view while the table scrolls.
//  - Back, at the top right, returns to the screen History was opened from; the
//    banner's History button shows as selected meanwhile (decision 58).
//  - (1 Nov 2026, decision 165) Print, beside Hosts and Back, for everyone who can open
//    History, phones included: the device's own Print (which also offers Save as PDF).
//    It prints what is on screen: the table in its current order, or Hosts while Hosts
//    is open. A4 portrait, black and white, no colour key, no buttons; the calendar
//    name and print date at the top, page numbers at the bottom, column headings
//    repeated on every page. Built as a print-only copy outside the app's own page, so
//    the banner and buttons never reach the paper.
//  - (decision 165) "Show meetings from: All years / 2026 / 2025 / …" above the table
//    filters the screen, so Print prints what is shown. The next meeting is always kept.
//    Hosts always counts every meeting, whatever year is chosen.
//  - (decision 165) "Hide the dishes" / "Show the dishes" hides or shows all the course
//    columns together (calendars that show the menu). Hidden to start on phones, shown
//    on wider screens; each person's choice is remembered on that device.
//  - (decision 166) "Add the hosts to the print": a tick box beside it; when ticked, the
//    Hosts summary (all meetings) prints after the meetings, on a new page only if it
//    doesn't fit. Remembered on that device.
//  - (decision 167) "Print abbreviated form", paper only, remembered on that device: no
//    dishes; in date order the years stay newest first but each year's dates run first
//    to last (the next meeting last in its year); one column headed by the index
//    (Country) reading "Indonesia (Sumatra)" where the keyword's value differs.
//  - (decision 168) "Append the countries list", on calendars whose History qualifier is
//    "Country": a tick box (remembered on that device) with From [A] To [Z] letters,
//    the From letter starting at the next meeting's country. When ticked, the print
//    ends with a world list (src/utils/worldCountries.js) by letter, after the hosts
//    when they are added: each country with its region and when the club last did it
//    (from the History; the next meeting marked "next"). Paper only. A country in the
//    History but not in the list is added to it; the club's own name for a country
//    ("Burma", "Korea") replaces the list's.
//  - (decision 165) Columns on larger screens and on paper: Year, Date, Host, the index
//    column (Country), the keyword's column (Culinary provenance), then the dishes. The
//    Year cell is merged down beside consecutive meetings of the same year (all of a
//    year's meetings in date order; single years when sorted otherwise). Phone cards
//    are unchanged.
//  - (4 Nov 2026, decisions 171-173) A hosting-schedule month marked "No meeting" comes
//    here once it drops off the schedule: one grey row for the month ("October 2026"),
//    "No meeting" and its note in the Host column, sorted as the 1st of the month, with
//    its own key entry. Not counted as a meeting, not in Hosts, not correctable.

// The sortable columns: Date, Host, then the index's and the keyword's (when the
// calendar has them). (decision 165) Host before them; the index before the keyword.
const sortKeysFor = (keywordLabel, indexLabel) => [
  { key: 'date', label: 'Date' },
  { key: 'host', label: 'Host' },
  ...(indexLabel ? [{ key: 'indexValue', label: indexLabel }] : []),
  ...(keywordLabel ? [{ key: 'keywordValue', label: keywordLabel }] : []),
];

// (decision 165) How many rows each Year cell spans: n on the first row of a run of
// meetings in the same year, 0 on the rows it covers, -1 on a row with no Year cell (an
// open correction box, which takes the whole width).
const yearSpansOf = (rows, isOpen) => {
  const spans = [];
  let start = -1;
  rows.forEach((m, i) => {
    if (isOpen(m)) { spans[i] = -1; start = -1; return; }
    if (start >= 0 && rows[start].date.slice(0, 4) === m.date.slice(0, 4)) {
      spans[start] += 1;
      spans[i] = 0;
    } else {
      start = i;
      spans[i] = 1;
    }
  });
  return spans;
};

// (decision 68) How often each host has hosted: [{ host, count, last }], most first.
const hostsSummaryOf = (meetings) => {
  const byKey = new Map();
  meetings.filter((m) => !m.next && m.host).forEach((m) => {
    const key = m.host.trim().toLowerCase();
    const cur = byKey.get(key) || { host: m.host.trim(), count: 0, last: '' };
    cur.count += 1;
    if (m.date > cur.last) { cur.last = m.date; cur.host = m.host.trim(); }
    byKey.set(key, cur);
  });
  return [...byKey.values()].sort((a, b) => b.count - a.count || compareText(a.host, b.host));
};

const compareText = (a, b) =>
  String(a || '').localeCompare(String(b || ''), 'en', { sensitivity: 'base' });

const sortMeetings = (meetings, sort) => {
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...meetings].sort((a, b) => {
    let c;
    if (sort.key === 'date') {
      c = a.date.localeCompare(b.date);
    } else {
      // Blanks always last, whichever way round.
      const av = a[sort.key];
      const bv = b[sort.key];
      if (!av && bv) return 1;
      if (av && !bv) return -1;
      c = compareText(av, bv);
    }
    if (c === 0) return b.date.localeCompare(a.date); // then newest first
    return c * dir;
  });
};

// A course's dishes as one line of text for the edit boxes; who brought each dish
// is kept in brackets (decision 59).
const courseText = (items) =>
  (items || []).map((d) => (d.name ? `${d.description} (${d.name})` : d.description)).join('; ');

// (10 Oct 2026, decision 80) A recorded dish ending in a bracketed name, e.g.
// "Sauerkraut goulash (Turea & Allan)", shows as "Sauerkraut goulash by Turea & Allan",
// like a dish from a poll, but only when the name in brackets is a known one: a host in
// this history or a name already on a poll dish. Anything else in brackets, e.g.
// "Pastel del Choclo (vegetarian)", stays as written. Display only: the record keeps
// the text as typed, so the correction box still shows the brackets.
const BRACKETED_NAME = /^(.*\S)\s*\((?:by\s+)?([^()]+)\)\s*$/i; // (decision 81) "(by …)" too
const knownNamesOf = (meetings) => {
  const names = new Set();
  (meetings || []).forEach((m) => {
    if (m.host && m.host.trim().toLowerCase() !== 'online') names.add(m.host.trim().toLowerCase());
    Object.values(m.menu || {}).forEach((items) => (items || []).forEach((d) => {
      if (d && d.name) names.add(String(d.name).trim().toLowerCase());
    }));
  });
  return names;
};
const withContributor = (d, knownNames) => {
  if (!d || d.name || !knownNames) return d;
  const match = BRACKETED_NAME.exec(String(d.description || ''));
  if (!match || !knownNames.has(match[2].trim().toLowerCase())) return d;
  return { ...d, description: match[1], name: match[2].trim() };
};

const Dishes = ({ items, empty, knownNames }) => {
  if (!items || items.length === 0) return empty;
  return (
    <ul className="space-y-0.5">
      {items.map((raw, i) => {
        const d = withContributor(raw, knownNames);
        return (
          <li key={i}>
            {d.description}
            {d.name ? <span className="text-gray-400"> by {d.name}</span> : null}
          </li>
        );
      })}
    </ul>
  );
};

// (decision 63) Row colours by kind of meeting.
// (10 Oct 2026, decision 71) A little stronger than the first build (blue-50/green-50 were
// hard to tell from white); the key's swatches use the same colours as the rows, with a
// firmer border, so the key still matches what it describes.
// (4 Nov 2026, decision 173) grey = a month with no meeting, from the hosting schedule
// (shown once the month has dropped off the schedule). Its key entry appears only when
// the history has one.
const rowKind = (m) => (m.next ? 'next' : m.cancelled ? 'cancelled' : m.hasEvent ? 'event' : 'recorded');
const ROW_BG = { next: 'bg-blue-100', event: 'bg-green-100', recorded: 'bg-white', cancelled: 'bg-gray-100' };
const CARD_BORDER = { next: 'border-blue-400', event: 'border-green-400', recorded: 'border-gray-300', cancelled: 'border-gray-300' };
const KEY = [
  { kind: 'next', label: 'Next meeting', swatch: 'bg-blue-100 border-blue-400' },
  { kind: 'event', label: 'From a calendar event', swatch: 'bg-green-100 border-green-400' },
  { kind: 'recorded', label: 'Recorded in the history only', swatch: 'bg-white border-gray-400' },
  { kind: 'cancelled', label: 'No meeting that month', swatch: 'bg-gray-100 border-gray-400' },
];

// (decision 173) "October 2026", for a month with no meeting.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const monthOfIso = (iso) => `${MONTHS[Number(String(iso).slice(5, 7)) - 1]} ${String(iso).slice(0, 4)}`;
// "No meeting · Cancelled" (the note left out when it only repeats "No meeting").
const noMeetingText = (m) => (m.note && m.note.trim().toLowerCase() !== 'no meeting'
  ? `No meeting · ${m.note.trim()}` : 'No meeting');

// "The Culinary provenance, Country and host" / "The host".
const eventPartsText = (keywordLabel, indexLabel) => {
  const parts = [keywordLabel, indexLabel].filter(Boolean);
  return parts.length === 0 ? 'The host' : `The ${parts.join(', ')} and host`;
};

// How a meeting is named in messages: its keyword value, else its date.
const meetingName = (m, formatDate) => m.keywordValue || `The meeting of ${formatDate(m.date)}`;

// (1 Nov 2026, decision 165) Remembered on this device: whether the dishes are shown.
const DISHES_PREF_KEY = 'ch_history_show_dishes';
const HOSTS_PRINT_PREF_KEY = 'ch_history_print_hosts'; // (decision 166)
const COUNTRIES_PRINT_PREF_KEY = 'ch_history_print_countries'; // (decision 168)
const ABBREV_PRINT_PREF_KEY = 'ch_history_print_abbreviated'; // (decision 167)

// (decision 167) "Indonesia (Sumatra)": the country, then the keyword's value in brackets
// when it differs (ignoring capitals); the keyword's value alone when there is no country.
const countryWithProvenance = (m) => {
  const country = String(m.indexValue || '').trim();
  const prov = String(m.keywordValue || '').trim();
  if (!country) return prov || '\u2014';
  if (!prov || prov.toLowerCase() === country.toLowerCase()) return country;
  return `${country} (${prov})`;
};
const initialShowDishes = () => {
  try {
    const saved = window.localStorage.getItem(DISHES_PREF_KEY);
    if (saved === 'shown') return true;
    if (saved === 'hidden') return false;
  } catch (_) { /* storage blocked: fall back to the screen width */ }
  try {
    // Phones (the card layout, below Tailwind's md width) start with the dishes hidden.
    return window.matchMedia('(min-width: 768px)').matches;
  } catch (_) {
    return true;
  }
};

// (decision 165) The print copy lives in its own element directly under <body>; when
// printing, everything else under <body> is left off the paper.
// (decision 168) Names compared without accents or capitals.
// "&" reads as "and" (Hot Club's "Trinidad & Tobago").
const plainName = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/\s*&\s*/g, ' and ').replace(/\s+/g, ' ').trim().toLowerCase();
const canonicalCountry = (s) => {
  const key = plainName(s);
  return plainName(COUNTRY_ALIASES[key] || key);
};
const letterOf = (c) => (plainName(c.sort || c.name).charAt(0) || '#').toUpperCase();

const PRINT_ROOT_CLASS = 'ch-history-print-root';
const PRINT_CSS = `
.${PRINT_ROOT_CLASS} { display: none; }
@media print {
  @page { size: A4 portrait; margin: 14mm 12mm 16mm 12mm;
    @bottom-center { content: "Page " counter(page) " of " counter(pages); font: 9pt Arial, Helvetica, sans-serif; color: #000; } }
  body > *:not(.${PRINT_ROOT_CLASS}) { display: none !important; }
  html, body { background: #fff !important; }
  .${PRINT_ROOT_CLASS} { display: block !important; color: #000; font: 10pt Arial, Helvetica, sans-serif; }
  .${PRINT_ROOT_CLASS} h1 { font-size: 15pt; font-weight: bold; margin: 0 0 2pt 0; }
  .${PRINT_ROOT_CLASS} .ch-print-sub { font-size: 9pt; margin: 0 0 8pt 0; }
  .${PRINT_ROOT_CLASS} table { width: calc(100% - 3pt); border-collapse: collapse; }
  .${PRINT_ROOT_CLASS} table.ch-print-hosts { width: auto; min-width: 50%; }
  .${PRINT_ROOT_CLASS} .ch-print-hosts-after { break-inside: avoid; page-break-inside: avoid; margin-top: 14pt; }
  .${PRINT_ROOT_CLASS} table.ch-print-countries { width: 100%; }
  .${PRINT_ROOT_CLASS} table.ch-print-countries th { border: none; border-bottom: 0.75pt solid #000; }
  .${PRINT_ROOT_CLASS} table.ch-print-countries td { border: none; padding: 1.5pt 5pt; }
  .${PRINT_ROOT_CLASS} table.ch-print-countries tbody { break-inside: avoid; page-break-inside: avoid; }
  .${PRINT_ROOT_CLASS} table.ch-print-countries td.ch-print-letter { text-align: center; font-weight: bold; font-size: 12pt; padding-top: 9pt; }
  .${PRINT_ROOT_CLASS} .ch-print-countries-after { margin-top: 14pt; }
  .${PRINT_ROOT_CLASS} h2 { font-size: 12pt; font-weight: bold; margin: 0 0 5pt 0; }
  .${PRINT_ROOT_CLASS} thead { display: table-header-group; }
  .${PRINT_ROOT_CLASS} tr { break-inside: avoid; page-break-inside: avoid; }
  .${PRINT_ROOT_CLASS} th { text-align: left; font-weight: bold; border: 0.75pt solid #000; padding: 3pt 5pt; vertical-align: middle; }
  .${PRINT_ROOT_CLASS} td { border-left: 0.75pt solid #000; border-right: 0.75pt solid #000; padding: 1.5pt 5pt; vertical-align: top; }
  .${PRINT_ROOT_CLASS} tbody tr:last-child td, .${PRINT_ROOT_CLASS} td.ch-print-year { border-bottom: 0.75pt solid #000; }
  .${PRINT_ROOT_CLASS} tr.ch-print-group td { border-top: 0.75pt solid #000; }
  .${PRINT_ROOT_CLASS} table.ch-print-dishes tr:not(.ch-print-group) td:not(.ch-print-yearcol) { border-top: 0.4pt solid #aaa; }
  .${PRINT_ROOT_CLASS} td.ch-print-yearcol { text-align: center; font-weight: bold; }
  .${PRINT_ROOT_CLASS} table.ch-print-dishes td { padding-top: 3pt; padding-bottom: 3pt; }
  .${PRINT_ROOT_CLASS} table.ch-print-hosts td { border: 0.75pt solid #000; padding: 3pt 5pt; }
  .${PRINT_ROOT_CLASS} .ch-print-num { text-align: right; }
  .${PRINT_ROOT_CLASS} .ch-print-year { text-align: center !important; }
  .${PRINT_ROOT_CLASS} td.ch-print-year { vertical-align: middle; border: 0.75pt solid #000; }
  .${PRINT_ROOT_CLASS} .ch-print-nowrap { white-space: nowrap; }
}
`;

const inputClass = 'w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm bg-white';

// The correction form for one meeting.
const EditMeeting = ({ meeting, courses, keywordLabel, indexLabel, today, formatDate, saving, error, onSave, onCancel }) => {
  const recorded = meeting.source === 'log';
  const extraCourses = courses.filter((c) => !['Starter', 'Main', 'Dessert'].includes(c));
  const [draft, setDraft] = useState(() => ({
    date: meeting.date,
    keywordValue: meeting.keywordValue || '',
    indexValue: meeting.indexValue || '',
    host: meeting.host || '',
    starter: courseText(meeting.menu.Starter),
    main: courseText(meeting.menu.Main),
    dessert: courseText(meeting.menu.Dessert),
    // Any further courses are kept together in one "Other" box.
    other: extraCourses
      .map((c) => {
        const t = courseText(meeting.menu[c]);
        if (!t) return '';
        return c === 'Other' ? t : `${c}: ${t}`;
      })
      .filter(Boolean)
      .join('; '),
  }));
  const set = (key) => (e) => setDraft({ ...draft, [key]: e.target.value });
  const showOther = extraCourses.length > 0 || draft.other !== '';
  const yesterday = (() => {
    const d = new Date(`${today}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
  })();

  const field = (label, key, multiline) => (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
      {multiline
        ? <textarea value={draft[key]} onChange={set(key)} rows={2} className={inputClass} />
        : <input type="text" value={draft[key]} onChange={set(key)} className={inputClass} />}
    </label>
  );

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
      <p className="font-semibold text-gray-800">Correct this meeting</p>
      {recorded ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* (10 Oct 2026) iPad Safari drew the date picker taller and wider than the other
              boxes, spilling into the next column: drawn as a plain box that keeps to its
              own column and height. */}
          <label className="block min-w-0">
            <span className="block text-xs font-semibold text-gray-600 mb-1">Date</span>
            <input
              type="date"
              value={draft.date}
              max={yesterday}
              onChange={set('date')}
              className={`${inputClass} block min-w-0 max-w-full appearance-none`}
              style={{ WebkitAppearance: 'none', minHeight: '2.5rem', lineHeight: '1.25rem' }}
            />
          </label>
          {keywordLabel && field(keywordLabel, 'keywordValue')}
          {indexLabel && field(indexLabel, 'indexValue')}
          {field('Host', 'host')}
        </div>
      ) : (
        <p className="text-sm text-gray-600">
          {formatDate(meeting.date)}{meeting.keywordValue ? ` · ${meeting.keywordValue}` : ''}
          {meeting.indexValue ? ` (${meeting.indexValue})` : ''}{meeting.host ? ` · ${meeting.host}` : ''}.
          {' '}{eventPartsText(keywordLabel, indexLabel)} come from the event: change them with Edit Event on the dashboard.
        </p>
      )}
      {courses.length > 0 && (
        <>
          <p className="text-xs text-gray-500">Separate dishes with a semicolon, e.g. "Rendang (Tim &amp; Teri); Nasi goreng".</p>
          {field('Starter', 'starter', true)}
          {field('Main', 'main', true)}
          {field('Dessert', 'dessert', true)}
          {showOther && field('Other', 'other', true)}
        </>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onSave(draft)}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const HistoryPanel = ({ calendar, apiBaseUrl, authToken, formatEventDate, onStatusMessage, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null); // { courses, meetings, canEdit, today, keywordLabel, indexLabel, showMenu }
  const [showHosts, setShowHosts] = useState(false);
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [editingDate, setEditingDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [deleting, setDeleting] = useState(null); // the meeting awaiting "Delete history record?"
  const [isDeleting, setIsDeleting] = useState(false);
  // (1 Nov 2026, decision 165) Year filter ('' = all years) and the dishes switch.
  const [yearFrom, setYearFrom] = useState('');
  const [showDishes, setShowDishes] = useState(initialShowDishes);
  // (1 Nov 2026, decision 166) "Add the hosts to the print", remembered on this device.
  const [addHostsToPrint, setAddHostsToPrint] = useState(() => {
    try { return window.localStorage.getItem(HOSTS_PRINT_PREF_KEY) === 'yes'; } catch (_) { return false; }
  });
  // (1 Nov 2026, decision 167) "Print abbreviated form", remembered on this device.
  const [printAbbrev, setPrintAbbrev] = useState(() => {
    try { return window.localStorage.getItem(ABBREV_PRINT_PREF_KEY) === 'yes'; } catch (_) { return false; }
  });
  const toggleAbbrevPrint = (checked) => {
    try { window.localStorage.setItem(ABBREV_PRINT_PREF_KEY, checked ? 'yes' : 'no'); } catch (_) { /* not remembered */ }
    setPrintAbbrev(checked);
  };
  // (1 Nov 2026, decision 168) "Append the countries list", remembered on this device,
  // and its letter range ('' = default).
  const [showCountries, setShowCountries] = useState(() => {
    try { return window.localStorage.getItem(COUNTRIES_PRINT_PREF_KEY) === 'yes'; } catch (_) { return false; }
  });
  const toggleCountriesInPrint = (checked) => {
    try { window.localStorage.setItem(COUNTRIES_PRINT_PREF_KEY, checked ? 'yes' : 'no'); } catch (_) { /* not remembered */ }
    setShowCountries(checked);
  };
  const [letterFrom, setLetterFrom] = useState('');
  const [letterTo, setLetterTo] = useState('');
  const toggleHostsInPrint = (checked) => {
    try { window.localStorage.setItem(HOSTS_PRINT_PREF_KEY, checked ? 'yes' : 'no'); } catch (_) { /* not remembered */ }
    setAddHostsToPrint(checked);
  };
  const toggleDishes = () => {
    setShowDishes((v) => {
      try { window.localStorage.setItem(DISHES_PREF_KEY, v ? 'hidden' : 'shown'); } catch (_) { /* not remembered */ }
      return !v;
    });
  };
  // (decision 165) The print copy's own element under <body>, there while History is open.
  const [printRoot] = useState(() => {
    if (typeof document === 'undefined') return null;
    const el = document.createElement('div');
    el.className = PRINT_ROOT_CLASS;
    return el;
  });
  useEffect(() => {
    if (!printRoot) return undefined;
    document.body.appendChild(printRoot);
    return () => { if (printRoot.parentNode) printRoot.parentNode.removeChild(printRoot); };
  }, [printRoot]);
  const editRef = useRef(null);
  const pageScrollRef = useRef(null); // (10 Oct 2026) page position when a correction box opened
  // (10 Oct 2026, decision 82) The heading (title, count, colour key, Hosts, Back) stays
  // pinned under the screen's banner while the page scrolls. The banner's height changes
  // with the screen width (its buttons wrap), so it is measured and followed.
  const [bannerHeight, setBannerHeight] = useState(0);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const banner = document.querySelector('div.sticky.top-0');
    if (!banner) return undefined;
    const measure = () => setBannerHeight(Math.round(banner.getBoundingClientRect().height));
    measure();
    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(measure);
      observer.observe(banner);
    } else {
      window.addEventListener('resize', measure);
    }
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', measure);
    };
  }, []);
  const calendarId = calendar ? calendar.id : null;

  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken || localStorage.getItem('auth_token')}`,
  });

  useEffect(() => {
    if (!calendarId) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await fetch(`${apiBaseUrl}/api/calendars/${calendarId}/calendar-history`, { headers: headers() });
        const d = await res.json().catch(() => null);
        if (cancelled) return;
        if (!res.ok) setLoadError((d && d.error) || 'Could not load the history.');
        else if (!d || d.enabled !== true) setLoadError('This calendar does not keep a history.');
        else {
          setData({
            courses: d.showMenu === true ? (d.courses || ['Starter', 'Main', 'Dessert']) : [],
            keywordLabel: d.keywordLabel || null,
            indexLabel: d.indexLabel || null,
            showMenu: d.showMenu === true,
            meetings: d.meetings || [],
            canEdit: d.canEdit === true,
            today: d.today || new Date().toISOString().slice(0, 10),
          });
        }
      } catch (_) {
        if (!cancelled) setLoadError('Could not load the history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarId, apiBaseUrl, reloadToken]);

  // Bring an opened correction form into view.
  useEffect(() => {
    if (editingDate && editRef.current && typeof editRef.current.scrollIntoView === 'function') {
      editRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [editingDate]);

  if (!calendar) return null;

  const pressHeading = (key) => {
    setSort((prev) => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'date' ? 'desc' : 'asc' }));
  };

  const meetings = data ? data.meetings : [];
  const next = meetings.find((m) => m.next) || null;
  // (decision 165) The years on offer, newest first; a year chosen keeps that year and
  // every later one. The next meeting is always kept.
  const years = [...new Set(meetings.filter((m) => !m.next).map((m) => m.date.slice(0, 4)))].sort().reverse();
  const yearOk = (m) => !yearFrom || m.date.slice(0, 4) >= yearFrom;
  const rest = sortMeetings(meetings.filter((m) => !m.next && yearOk(m)), sort);
  const rows = next ? [next, ...rest] : rest;
  const menuCourses = data ? data.courses : [];
  // (decision 165) The course columns, unless the dishes are hidden.
  const courses = showDishes ? menuCourses : [];
  const formatDate = (iso) => (formatEventDate ? formatEventDate(iso) : iso);
  // (decision 173) A month with no meeting shows its month, not a day.
  const dateText = (m) => (m.cancelled ? monthOfIso(m.date) : formatDate(m.date));
  const keywordLabel = data ? data.keywordLabel : null;
  const indexLabel = data ? data.indexLabel : null;
  const SORT_KEYS = sortKeysFor(keywordLabel, indexLabel);
  // A meeting from the app has only its dishes to correct here; without the menu, its
  // details are all corrected with Edit Event.
  // (decision 173) A month with no meeting has nothing to correct here.
  const canEditMeeting = (m) => !!data && data.canEdit && !m.next && !m.cancelled && m.date < data.today &&
    (m.source === 'log' || data.showMenu);
  const hosts = hostsSummaryOf(meetings);
  const knownNames = knownNamesOf(meetings); // (decision 80)
  // (decision 173) Months with no meeting are shown but not counted as meetings.
  const held = meetings.filter((m) => !m.next && !m.cancelled).length;
  const shown = rest.filter((m) => !m.cancelled).length; // (decision 165) meetings held within the chosen years
  const keyShown = KEY.filter((k) => k.kind !== 'cancelled' || meetings.some((m) => m.cancelled));
  // (decision 168) The countries list: the world list plus any country in the History
  // that it lacks; each with when the club last did it and whether it is next.
  const offersCountries = !!indexLabel && indexLabel.trim().toLowerCase() === 'country';
  const countriesOn = offersCountries && showCountries;
  const heldByCountry = new Map();
  meetings.forEach((m) => {
    if (!m.indexValue) return;
    const key = canonicalCountry(m.indexValue);
    const cur = heldByCountry.get(key) || { last: '', next: '', name: m.indexValue.trim() };
    if (m.next) cur.next = m.date;
    else if (m.date > cur.last) cur.last = m.date;
    heldByCountry.set(key, cur);
  });
  const allCountries = (() => {
    const list = WORLD_COUNTRIES.map((c) => ({ ...c }));
    const known = new Set(list.map((c) => plainName(c.name)));
    heldByCountry.forEach((v, key) => { if (!known.has(key)) list.push({ name: v.name, region: '' }); });
    list.forEach((c) => {
      const h = heldByCountry.get(plainName(c.name));
      c.last = h ? h.last : '';
      c.next = h ? h.next : '';
      // The club's own name for a country it has done ("Burma", "East Timor", "Korea")
      // replaces the list's, and is filed under its own letter.
      if (h && plainName(h.name) !== plainName(c.name)) { c.name = h.name; delete c.sort; }
      c.letter = letterOf(c);
    });
    return list.sort((a, b) => compareText(plainName(a.sort || a.name), plainName(b.sort || b.name)));
  })();
  const countryLetters = [...new Set(allCountries.map((c) => c.letter))];
  const nextCountry = next && next.indexValue
    ? allCountries.find((c) => plainName(c.name) === canonicalCountry(next.indexValue)) : null;
  const fromLetter = letterFrom || (nextCountry ? nextCountry.letter : countryLetters[0] || 'A');
  const toLetter = letterTo || countryLetters[countryLetters.length - 1] || 'Z';
  const [lowLetter, highLetter] = fromLetter <= toLetter ? [fromLetter, toLetter] : [toLetter, fromLetter];
  const countryGroups = countryLetters
    .filter((l) => l >= lowLetter && l <= highLetter)
    .map((l) => ({ letter: l, items: allCountries.filter((c) => c.letter === l) }));
  const countriesShown = countryGroups.reduce((n, g) => n + g.items.length, 0);
  const lastHeldText = (c) => [c.last ? formatDate(c.last) : null, c.next ? `next ${formatDate(c.next)}` : null]
    .filter(Boolean).join(' · ');
  const countText = yearFrom
    ? `${shown} of ${held} meetings, from ${yearFrom}`
    : `${held} meeting${held === 1 ? '' : 's'}`;

  const canDeleteMeeting = (m) => !!data && data.canEdit && m.source === 'log' && !m.hasEvent;
  const confirmDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendars/${calendarId}/calendar-history/${deleting.date}`, {
        method: 'DELETE',
        headers: headers(),
      });
      const d = await res.json().catch(() => null);
      if (!res.ok) {
        if (typeof onStatusMessage === 'function') onStatusMessage((d && d.error) || 'Could not delete the meeting', 'error', 6000);
        return;
      }
      // (10 Oct 2026, decision 78) "History record of 9-Oct-2026 deleted." on every calendar.
      if (typeof onStatusMessage === 'function') onStatusMessage(`History record of ${formatDate(deleting.date)} deleted.`, 'success');
      setDeleting(null);
      setReloadToken((t) => t + 1);
    } catch (_) {
      if (typeof onStatusMessage === 'function') onStatusMessage('Could not delete the meeting', 'error', 6000);
    } finally {
      setIsDeleting(false);
    }
  };

  // (10 Oct 2026) Opening the correction box scrolls the page to it, which took the
  // heading (Hosts, Back) off the top of the screen and left it there after Cancel or
  // Save. The page now returns to where it was when the pencil was pressed; the table
  // keeps its own scroll position, so the meeting just corrected stays in view.
  const restorePageScroll = () => {
    const y = pageScrollRef.current;
    pageScrollRef.current = null;
    if (typeof y !== 'number' || typeof window === 'undefined') return;
    const go = () => { try { window.scrollTo({ top: y }); } catch (_) { window.scrollTo(0, y); } };
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(go);
    else go();
  };
  const startEdit = (m) => {
    if (typeof window !== 'undefined') pageScrollRef.current = window.scrollY;
    setSaveError(null);
    setEditingDate(m.date);
  };
  const cancelEdit = () => { setSaveError(null); setEditingDate(null); restorePageScroll(); };
  const saveEdit = async (meeting, draft) => {
    if (keywordLabel && meeting.source === 'log' && !draft.keywordValue.trim()) {
      setSaveError(`Please give the ${keywordLabel}.`);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const body = {
        keywordValue: meeting.source === 'log' ? draft.keywordValue : meeting.keywordValue,
        indexValue: meeting.source === 'log' ? draft.indexValue : meeting.indexValue,
        host: draft.host,
        starter: draft.starter,
        main: draft.main,
        dessert: draft.dessert,
        other: draft.other,
      };
      if (meeting.source === 'log' && draft.date !== meeting.date) body.newDate = draft.date;
      const res = await fetch(`${apiBaseUrl}/api/calendars/${calendarId}/calendar-history/${meeting.date}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => null);
      if (!res.ok) {
        setSaveError((d && d.error) || 'Could not save the correction.');
        return;
      }
      setEditingDate(null);
      restorePageScroll();
      if (typeof onStatusMessage === 'function') onStatusMessage(`${meetingName({ ...meeting, keywordValue: (meeting.source === 'log' ? draft.keywordValue : meeting.keywordValue || '').trim() }, formatDate)} corrected`, 'success');
      setReloadToken((t) => t + 1);
    } catch (_) {
      setSaveError('Could not save the correction.');
    } finally {
      setSaving(false);
    }
  };

  const sortValue = `${sort.key}:${sort.dir}`;
  const sortOptions = [
    { value: 'date:desc', label: 'Date (newest first)' },
    { value: 'date:asc', label: 'Date (oldest first)' },
    ...SORT_KEYS.filter((k) => k.key !== 'date').flatMap((k) => [
      { value: `${k.key}:asc`, label: `${k.label} (A to Z)` },
      { value: `${k.key}:desc`, label: `${k.label} (Z to A)` },
    ]),
  ];

  const nextBadge = (
    <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-600 text-white align-middle">Next</span>
  );
  const pencil = (m) => (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => startEdit(m)}
        className="p-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
        aria-label={`Correct ${meetingName(m, formatDate)}`}
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      {canDeleteMeeting(m) && (
        <button
          type="button"
          onClick={() => setDeleting(m)}
          className="p-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
          aria-label={`Delete ${meetingName(m, formatDate)}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
  const editor = (m) => (
    <div ref={editRef}>
      <EditMeeting
        key={m.date}
        meeting={m}
        courses={menuCourses /* (decision 165) the dish boxes stay, even with the dishes hidden */}
        keywordLabel={keywordLabel}
        indexLabel={indexLabel}
        today={data.today}
        formatDate={formatDate}
        saving={saving}
        error={saveError}
        onSave={(draft) => saveEdit(m, draft)}
        onCancel={cancelEdit}
      />
    </div>
  );
  const editColumn = !!data && data.canEdit;

  // (1 Nov 2026, decision 165) The print copy: what is on screen, in plain black and white.
  const printHosts = !!data && showHosts && hosts.length > 0;
  const todayIso = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  // (1 Nov 2026, decision 167) The abbreviated print: no dishes; in date order (newest
  // first) the years stay newest first but the dates within each year run first to
  // last, the next meeting in its place at the end of its year; the index and keyword
  // columns become one, headed by the index ("Indonesia (Sumatra)"). Paper only: the
  // screen is unchanged.
  const abbrevByYear = printAbbrev && sort.key === 'date' && sort.dir === 'desc';
  const printRows = abbrevByYear
    ? [...rows].sort((a, b) => b.date.slice(0, 4).localeCompare(a.date.slice(0, 4)) || a.date.localeCompare(b.date))
    : rows;
  const printCourses = printAbbrev ? [] : courses;
  const combineCountry = printAbbrev && !!indexLabel && !!keywordLabel;
  const printKeys = combineCountry ? SORT_KEYS.filter((k) => k.key !== 'keywordValue') : SORT_KEYS;
  const sortLabel = abbrevByYear
    ? 'Year (newest first), each year in date order'
    : (sortOptions.find((o) => o.value === sortValue) || {}).label;
  // (decision 78) The printed date kept on one line.
  const printedOn = <span className="ch-print-nowrap">printed on {formatDate(todayIso)}</span>;
  const printSub = printHosts
    ? <>Hosts: meetings held, the next one not counted · {printedOn}</>
    : <>{[countText, sortLabel ? `sorted by ${sortLabel.charAt(0).toLowerCase()}${sortLabel.slice(1)}` : null,
      printAbbrev ? 'abbreviated form' : null].filter(Boolean).join(' · ')} · {printedOn}</>;
  const printYearSpans = yearSpansOf(printRows, () => false);
  const yearSpans = yearSpansOf(rows, (m) => editingDate === m.date);
  const printDishes = (items) => (items || []).map((raw, i) => {
    const d = withContributor(raw, knownNames);
    return <div key={i}>{d.description}{d.name ? ` by ${d.name}` : ''}</div>;
  });
  const printHostsTable = (
    <table className="ch-print-hosts">
      <thead>
        <tr><th>Host</th><th className="ch-print-num">Hosted</th><th>Last hosted</th></tr>
      </thead>
      <tbody>
        {hosts.map((h) => (
          <tr key={h.host}>
            <td>{h.host}</td>
            <td className="ch-print-num">{h.count}</td>
            <td className="ch-print-nowrap">{formatDate(h.last)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  const printView = (
    <>
      <style>{PRINT_CSS}</style>
      {data && (printHosts ? (
        <>
          <h1>{calendar.name} history: hosts</h1>
          <p className="ch-print-sub">{printSub}</p>
          {printHostsTable}
        </>
      ) : (
        <>
          <h1>{calendar.name} history</h1>
          <p className="ch-print-sub">{printSub}</p>
          <table className={printCourses.length > 0 ? 'ch-print-dishes' : undefined}>
            <thead>
              <tr>
                <th className="ch-print-year">Year</th>
                {printKeys.map(({ key, label }) => <th key={key}>{label}</th>)}
                {printCourses.map((course) => <th key={course}>{course}</th>)}
              </tr>
            </thead>
            <tbody>
              {printRows.map((m, i) => (
                <tr key={`${m.source}-${m.date}`} className={printYearSpans[i] > 0 ? 'ch-print-group' : undefined}>
                  {/* With the dishes shown, rows are tall and a merged cell spanning pages
                      upsets the browser's page breaks: the year then stands once at the top
                      of its block, in a column ruled only round each year. */}
                  {printCourses.length > 0 ? (
                    <td className="ch-print-yearcol">{printYearSpans[i] > 0 ? m.date.slice(0, 4) : ''}</td>
                  ) : printYearSpans[i] > 0 && (
                    <td rowSpan={printYearSpans[i]} className="ch-print-year">{m.date.slice(0, 4)}</td>
                  )}
                  <td className="ch-print-nowrap">{dateText(m)}{m.next ? ' (next)' : ''}</td>
                  <td>{m.cancelled ? noMeetingText(m) : (m.host || '—')}</td>
                  {combineCountry ? <td>{countryWithProvenance(m)}</td> : (
                    <>
                      {indexLabel && <td>{m.indexValue || '—'}</td>}
                      {keywordLabel && <td>{m.keywordValue}</td>}
                    </>
                  )}
                  {printCourses.map((course) => (
                    <td key={course}>{m.next || !(m.menu[course] || []).length ? '—' : printDishes(m.menu[course])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {/* (1 Nov 2026, decision 166) "Add the hosts to the print": the Hosts summary
              after the meetings, on a new page only if it doesn't fit. It counts every
              meeting, whatever years are printed above it (decision 165, 10(a)). */}
          {addHostsToPrint && hosts.length > 0 && (
            <div className="ch-print-hosts-after">
              <h2>Hosts: all meetings held, the next one not counted</h2>
              {printHostsTable}
            </div>
          )}
          {/* (1 Nov 2026, decision 168) "Append the countries list": after the hosts. */}
          {countriesOn && countryGroups.length > 0 && (
            <div className="ch-print-countries-after">
              <h2>Countries {lowLetter} to {highLetter}</h2>
              <p className="ch-print-sub">{countriesShown} countries · last held from the history</p>
              <table className="ch-print-countries">
                <thead>
                  <tr><th>Country</th><th>Region</th><th>Last held</th></tr>
                </thead>
                {countryGroups.map((g) => (
                  <tbody key={g.letter}>
                    <tr><td colSpan={3} className="ch-print-letter">{g.letter}</td></tr>
                    {g.items.map((c) => (
                      <tr key={c.name}>
                        <td>{c.name}</td>
                        <td>{c.region}</td>
                        <td className="ch-print-nowrap">{lastHeldText(c)}</td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          )}
        </>
      ))}
    </>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="bg-white rounded-xl shadow-sm">
        <div
          className="sticky z-[5] bg-white rounded-t-xl px-4 sm:px-6 py-4 border-b flex items-start justify-between gap-3"
          style={{ top: bannerHeight }}
        >
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{calendar.name} history</h2>
            {data && <p className="text-xs text-gray-500">{showHosts ? `${held} meeting${held === 1 ? '' : 's'}` : countText}</p>}
            {/* (10 Oct 2026, decision 70) No key while Hosts is open: it describes the
                history table's row colours, which the Hosts summary doesn't have. */}
            {data && !showHosts && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2" aria-label="Key">
                {keyShown.map((k) => (
                  <span key={k.kind} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                    <span className={`inline-block w-3.5 h-3.5 rounded border ${k.swatch}`} />
                    {k.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="shrink-0 flex flex-wrap justify-end items-center gap-2">
            {/* (1 Nov 2026, decision 165) Prints what is on screen. */}
            {data && (
              <button
                type="button"
                onClick={() => { try { window.print(); } catch (_) { /* no print on this device */ } }}
                className="px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Print
              </button>
            )}
            {data && hosts.length > 0 && (
              <button
                type="button"
                onClick={() => setShowHosts((v) => !v)}
                aria-pressed={showHosts}
                className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition ${showHosts
                  ? 'bg-blue-50 text-blue-700 border-blue-300'
                  : 'text-gray-700 hover:text-gray-900 border-gray-300 hover:bg-gray-50'}`}
              >
                Hosts
              </button>
            )}
            {typeof onBack === 'function' && (
              <button
                type="button"
                // (10 Oct 2026, decision 72) With Hosts open, Back closes Hosts and returns
                // to the history table; pressed again, it leaves the History page.
                onClick={() => (showHosts ? setShowHosts(false) : onBack())}
                className="px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Back
              </button>
            )}
          </div>
        </div>

        {/* (decision 68) How often each member has hosted. */}
        {/* (10 Oct 2026, decisions 70 and 72) Shown in place of the history table. */}
        {data && showHosts && hosts.length > 0 && (
          <div className="px-3 sm:px-6 py-4">
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-700">Hosts</p>
                <p className="text-xs text-gray-500">Meetings held, the next one not counted</p>
              </div>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-t border-gray-100">
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Host</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-600">Hosted</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Last hosted</th>
                  </tr>
                </thead>
                <tbody>
                  {hosts.map((h) => (
                    <tr key={h.host} className="border-t border-gray-100">
                      <td className="px-3 py-1.5 text-gray-800">{h.host}</td>
                      <td className="px-3 py-1.5 text-right text-gray-700">{h.count}</td>
                      <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{formatDate(h.last)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Hidden (not removed) while Hosts is open, so the sort order and scroll
            position are as they were when Back returns to it. */}
        <div className={`px-3 sm:px-6 py-4 ${data && showHosts && hosts.length > 0 ? 'hidden' : ''}`}>
          {loading && !data ? (
            <p className="text-sm text-gray-500">Loading the history…</p>
          ) : loadError ? (
            <p className="text-sm text-gray-600">{loadError}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500">No meetings recorded yet.</p>
          ) : (
            <>
              {/* (1 Nov 2026, decision 165) Which years to show (and print), and the dishes switch. */}
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <label className="block">
                  <span className="block text-xs font-semibold text-gray-600 mb-1">Show meetings from</span>
                  <select
                    value={yearFrom}
                    onChange={(e) => setYearFrom(e.target.value)}
                    className="px-3 py-2 border-2 border-gray-200 rounded-lg bg-white text-sm focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">All years</option>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </label>
                {menuCourses.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleDishes}
                    aria-pressed={!showDishes}
                    className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    {showDishes ? 'Hide the dishes' : 'Show the dishes'}
                  </button>
                )}
                {/* (1 Nov 2026, decision 166) */}
                {hosts.length > 0 && (
                  <label className="inline-flex items-center gap-2 py-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addHostsToPrint}
                      onChange={(e) => toggleHostsInPrint(e.target.checked)}
                      className="w-4 h-4"
                    />
                    Add the hosts to the print
                  </label>
                )}
                {/* (1 Nov 2026, decision 168) */}
                {offersCountries && (
                  <div className="inline-flex flex-wrap items-end gap-3">
                    <label className="inline-flex items-center gap-2 py-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showCountries}
                        onChange={(e) => toggleCountriesInPrint(e.target.checked)}
                        className="w-4 h-4"
                      />
                      Append the countries list
                    </label>
                    {countriesOn && [['From', fromLetter, setLetterFrom], ['To', toLetter, setLetterTo]].map(([label, value, setter]) => (
                      <label key={label} className="block">
                        <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
                        <select
                          value={value}
                          onChange={(e) => setter(e.target.value)}
                          className="px-3 py-2 border-2 border-gray-200 rounded-lg bg-white text-sm focus:border-blue-500 focus:outline-none"
                        >
                          {countryLetters.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </label>
                    ))}
                  </div>
                )}
                {/* (1 Nov 2026, decision 167) Paper only. */}
                <label className="inline-flex items-center gap-2 py-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={printAbbrev}
                    onChange={(e) => toggleAbbrevPrint(e.target.checked)}
                    className="w-4 h-4"
                  />
                  Print abbreviated form
                </label>
              </div>


              {/* Phones: cards, with a Sort by drop-down */}
              <div className="md:hidden space-y-3">
                <label className="block">
                  <span className="block text-xs font-semibold text-gray-600 mb-1">Sort by</span>
                  <select
                    value={sortValue}
                    onChange={(e) => {
                      const [key, dir] = e.target.value.split(':');
                      setSort({ key, dir });
                    }}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg bg-white text-sm focus:border-blue-500 focus:outline-none"
                  >
                    {sortOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                {rows.map((m) => (editingDate === m.date ? (
                  <div key={`${m.source}-${m.date}`}>{editor(m)}</div>
                ) : (
                  <div
                    key={`${m.source}-${m.date}`}
                    className={`border rounded-xl p-4 ${ROW_BG[rowKind(m)]} ${CARD_BORDER[rowKind(m)]}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {m.next && nextBadge}
                          {m.cancelled ? (
                            // (decision 173) "October 2026" with "No meeting" and its note below.
                            <p className="font-semibold text-gray-600">{dateText(m)}</p>
                          ) : keywordLabel ? (
                            <p className="font-semibold text-gray-800">{m.keywordValue}</p>
                          ) : (
                            <p className="font-semibold text-gray-800">{formatDate(m.date)}</p>
                          )}
                          {m.indexValue && m.indexValue !== m.keywordValue && (
                            <p className="text-sm text-gray-500">({m.indexValue})</p>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {m.cancelled
                            ? noMeetingText(m)
                            : [keywordLabel ? formatDate(m.date) : null, m.host].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      {canEditMeeting(m) && pencil(m)}
                    </div>
                    {!m.next && !m.cancelled && courses.length > 0 && (
                      <div className="space-y-2 mt-3">
                        {courses.map((course) => (
                          <div key={course}>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{course}</p>
                            <div className="text-sm text-gray-700">
                              <Dishes items={m.menu[course]} knownNames={knownNames} empty={<p className="text-sm text-gray-400 italic">Not recorded</p>} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )))}
              </div>

              {/* Larger screens: a table whose headings sort */}
              <div className="hidden md:block overflow-auto border border-gray-200 rounded-xl" style={{ maxHeight: '75vh' }}>
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {/* (decision 165) Year, merged down beside its meetings; not a sort of its own. */}
                      <th className="sticky top-0 z-[1] bg-gray-50 px-3 py-2 text-center font-semibold text-gray-600 whitespace-nowrap border-r border-gray-200">Year</th>
                      {SORT_KEYS.map(({ key, label }) => {
                        const active = sort.key === key;
                        return (
                          <th
                            key={key}
                            className="sticky top-0 z-[1] bg-gray-50 px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap"
                            aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          >
                            <button
                              type="button"
                              onClick={() => pressHeading(key)}
                              className={`inline-flex items-center gap-1 hover:text-blue-700 ${active ? 'text-blue-700' : ''}`}
                            >
                              {label}
                              {active && (sort.dir === 'asc'
                                ? <ArrowUp className="w-3.5 h-3.5" />
                                : <ArrowDown className="w-3.5 h-3.5" />)}
                            </button>
                          </th>
                        );
                      })}
                      {courses.map((course) => (
                        <th key={course} className="sticky top-0 z-[1] bg-gray-50 px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">{course}</th>
                      ))}
                      {editColumn && <th className="sticky top-0 z-[1] bg-gray-50 px-3 py-2" />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((m, i) => (editingDate === m.date ? (
                      <tr key={`${m.source}-${m.date}`} className="border-t border-gray-100">
                        <td colSpan={1 + SORT_KEYS.length + courses.length + (editColumn ? 1 : 0)} className="px-3 py-3">
                          {editor(m)}
                        </td>
                      </tr>
                    ) : (
                      <tr
                        key={`${m.source}-${m.date}`}
                        // (decision 165) A firmer line where each year's block starts.
                        className={`${yearSpans[i] > 0 ? 'border-t-2 border-gray-400' : 'border-t border-gray-100'} align-top ${ROW_BG[rowKind(m)]}`}
                      >
                        {yearSpans[i] > 0 && (
                          <td
                            rowSpan={yearSpans[i]}
                            className="px-3 py-2 bg-white text-center align-middle font-semibold text-gray-800 border-r-2 border-t-2 border-gray-400"
                          >
                            {m.date.slice(0, 4)}
                          </td>
                        )}
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {/* (9 Oct 2026) The next meeting's date sits inside the blue pill, so it
                              stays in line with the other dates (the pill's padding is taken back
                              with a negative margin). */}
                          {m.next ? (
                            <span
                              className="inline-block -ml-2 px-2 py-0.5 rounded-full bg-blue-600 text-white font-semibold"
                              aria-label={`Next meeting: ${formatDate(m.date)}`}
                            >
                              {formatDate(m.date)}
                            </span>
                          ) : dateText(m)}
                        </td>
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                          {m.cancelled
                            ? <span className="italic text-gray-600">{noMeetingText(m)}</span>
                            : (m.host || <span className="text-gray-400">—</span>)}
                        </td>
                        {indexLabel && <td className="px-3 py-2 text-gray-700">{m.indexValue || <span className="text-gray-400">—</span>}</td>}
                        {keywordLabel && <td className="px-3 py-2 font-medium text-gray-800">{m.keywordValue}</td>}
                        {courses.map((course) => (
                          <td key={course} className="px-3 py-2 text-gray-700">
                            {m.next
                              ? <span className="text-gray-400">—</span>
                              : <Dishes items={m.menu[course]} knownNames={knownNames} empty={<span className="text-gray-400">—</span>} />}
                          </td>
                        ))}
                        {editColumn && <td className="px-3 py-2">{canEditMeeting(m) && pencil(m)}</td>}
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

      </div>

      {/* (decision 61) Confirmation, like Delete Event. (10 Oct 2026, decision 76) Worded
          as "Delete history record?": "…the history record of 9-Oct-2026." (no keyword
          value) / "…the history record of Sumatra (26-Feb-2026)." */}
      {deleting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete history record?</h3>
            <p className="text-gray-600 mb-4">
              {/* (decision 78) The date kept on one line (it had broken at "9-Oct-"). */}
              This will permanently delete the history record of {deleting.keywordValue ? `${deleting.keywordValue} ` : ''}
              <span className="whitespace-nowrap">{deleting.keywordValue ? `(${formatDate(deleting.date)})` : formatDate(deleting.date)}</span>.
              {' '}This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleting(null)}
                disabled={isDeleting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* (1 Nov 2026, decision 165) The print copy, outside the app's own page. */}
      {printRoot && createPortal(printView, printRoot)}
    </div>
  );
};

export default HistoryPanel;
